import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, CircularProgress, Tooltip, Switch, FormControlLabel,
  Autocomplete, Checkbox as MuiCheckbox
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { subjectsAPI, settingsAPI } from '../services/api';

// max_mark and pass_mark removed — those are set per-assessment, not per-subject
const EMPTY = {
  name: '', code: '', class_ids: [], is_core: false,
  description: '', color: '#1976d2'
};

const COLORS = ['#1565c0','#e53935','#2e7d32','#f57c00','#6a1b9a','#00838f','#795548','#37474f'];

export default function Subjects() {
  const { enqueueSnackbar } = useSnackbar();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await subjectsAPI.getAll({ search });
      if (res.data.success) setSubjects(res.data.data);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data || []); });
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = (subject) => {
    setEditing(subject);
    setForm({
      name: subject.name || '', code: subject.code || '',
      class_ids: subject.class_ids ? (Array.isArray(subject.class_ids) ? subject.class_ids : subject.class_ids.split(',').map(Number).filter(Boolean)) : [],
      is_core: !!subject.is_core,
      description: subject.description || '', color: subject.color || '#1976d2'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) return enqueueSnackbar('Name and code are required', { variant: 'warning' });
    setSaving(true);
    try {
      const payload = { ...form, is_core: form.is_core ? 1 : 0, class_ids: form.class_ids };
      if (editing) await subjectsAPI.update(editing.id, payload);
      else await subjectsAPI.create(payload);
      enqueueSnackbar(editing ? 'Subject updated' : 'Subject created', { variant: 'success' });
      setDialogOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving subject', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    try {
      await subjectsAPI.delete(id);
      enqueueSnackbar('Subject deleted', { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error deleting subject', { variant: 'error' }); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Subjects Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Subject</Button>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <TextField placeholder="Search subjects..." value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ maxWidth: 400 }} />
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Colour</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Teachers</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
                : subjects.length === 0
                ? <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>No subjects found</TableCell></TableRow>
                : subjects.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: s.color || '#1976d2' }} />
                    </TableCell>
                    <TableCell><Typography fontWeight={700} fontSize={14}>{s.name}</Typography></TableCell>
                    <TableCell><Chip label={s.code} size="small" variant="outlined" /></TableCell>
                    <TableCell>{s.class_level || '—'}</TableCell>
                    <TableCell>
                      <Chip label={s.is_core ? 'Core' : 'Elective'} size="small"
                        color={s.is_core ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>{s.teacher_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(s.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField label="Subject Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Code *" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                helperText="e.g. MATH, ENG, SCI" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                multiple
                options={classes}
                getOptionLabel={c => c.stream ? `${c.name} (${c.stream})` : c.name}
                value={classes.filter(c => form.class_ids.includes(c.id))}
                onChange={(_, vals) => setForm({ ...form, class_ids: vals.map(c => c.id) })}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <MuiCheckbox size="small" checked={selected} sx={{ mr: 1 }} />
                    {option.name}{option.stream ? ` (${option.stream})` : ''}
                  </li>
                )}
                renderInput={params => (
                  <TextField {...params} label="Applicable Classes *" helperText="Select one or more classes this subject applies to" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={
                <Switch
                  checked={!!form.is_core}
                  onChange={e => setForm({ ...form, is_core: e.target.checked })}
                />
              } label="Core Subject" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Subject Colour (used in reports and charts)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {COLORS.map(c => (
                  <Box key={c} onClick={() => setForm({ ...form, color: c })}
                    sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: c, cursor: 'pointer',
                      border: form.color === c ? '3px solid #333' : '3px solid transparent',
                      transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.15)' } }} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description (optional)" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })} multiline rows={2} />
            </Grid>

            {/* Informational note about marks */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  ℹ️ Max marks and pass marks are set per-assessment in Assessments Management, not here.
                  This keeps subject setup simple and lets different assessments have different mark schemes.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
